import { type FarmResilienceApiResponse, type ResilienceTrendDirection } from "pages/app/resilience-score/utils/ResilienceScoreUtils";

export type BreakdownMetricRow = {
  label: string;
  value: number | null;
  trend: ResilienceTrendDirection;
  changePct: number | null;
};

export type BreakdownTimelinePoint = {
  year: number;
  value: number | null;
  unknown: boolean;
};

export type BreakdownCardProps = {
  title: string;
  score: number | null;
  maxScore: number;
  riskLevel: "low" | "medium" | "high" | null;
  changePct: number | null;
  metricRows: BreakdownMetricRow[];
  timeline: BreakdownTimelinePoint[];
  selectedYear: number;
};

export type ResilienceTimelineRow = {
  year: number;
  soilHealth: number;
  waterManagement: number;
  biodiversity: number;
  overall: number;
  unknown: boolean;
};

type PatternCacheContext = CanvasRenderingContext2D & {
  __resiliencePatternCache?: Record<string, CanvasPattern | string>;
};

export const getRiskIcon = (riskLevel: "low" | "medium" | "high" | null) => {
  if (!riskLevel) return "tabler/arrow-straight-right-circle";
  switch (riskLevel) {
    case "low":
      return "tabler/arrow-down-right-circle";
    case "medium":
      return "tabler/arrow-straight-right-circle";
    case "high":
      return "tabler/arrow-up-right-circle";
    default:
      return "tabler/arrow-straight-right-circle";
  }
};

export const formatScore = (value: number | undefined | null, fallback = "0") => {
  if (value == null || Number.isNaN(value)) return fallback;
  const truncated = Math.floor(value * 10) / 10;
  return truncated.toFixed(1);
};

export const avg = (values: Array<number | null>) => {
  const valid = values.filter((v): v is number => v != null && !Number.isNaN(v));
  if (!valid.length) return null;
  return valid.reduce((sum, v) => sum + v, 0) / valid.length;
};

export const RESILIENCE_SCORE_EXPLAINER_VIDEO_VIMEO_URL = "https://vimeo.com/1157309156/c9dd5c006a";

export const getDiagonalPattern = (ctx: CanvasRenderingContext2D, chartArea: { left: number; top: number } | undefined): CanvasPattern | string => {
  const context = ctx as PatternCacheContext;
  const cache = context.__resiliencePatternCache ?? {};
  const cacheKey = "unknown";
  if (cache[cacheKey]) return cache[cacheKey];

  const patternCanvas = document.createElement("canvas");
  patternCanvas.width = 24;
  patternCanvas.height = 24;
  const patternCtx = patternCanvas.getContext("2d");
  if (!patternCtx) return "#f6f6f6";

  patternCtx.fillStyle = "#f6f6f6";
  patternCtx.fillRect(0, 0, patternCanvas.width, patternCanvas.height);
  patternCtx.strokeStyle = "#999999";
  patternCtx.lineWidth = 1.5;
  patternCtx.lineCap = "round";

  for (let x = -24; x <= 48; x += 8) {
    patternCtx.beginPath();
    patternCtx.moveTo(x, patternCanvas.height);
    patternCtx.lineTo(x + patternCanvas.width, 0);
    patternCtx.stroke();
  }

  const pattern = ctx.createPattern(patternCanvas, "repeat") ?? "#f6f6f6";
  if (typeof pattern !== "string" && pattern.setTransform) {
    pattern.setTransform(new DOMMatrix().translate(chartArea?.left ?? 0, chartArea?.top ?? 0));
  }

  cache[cacheKey] = pattern;
  context.__resiliencePatternCache = cache;
  return pattern;
};

const YEAR_KEY_REGEX = /^\d{4}$/;

export const getAvailableResilienceScoreYears = (response: FarmResilienceApiResponse | null | undefined): number[] => {
  if (!response?.length) return [];

  const years = new Set<number>();
  response.forEach((field) => {
    Object.keys(field).forEach((key) => {
      if (!YEAR_KEY_REGEX.test(key)) return;
      const year = Number(key);
      if (Number.isFinite(year)) years.add(year);
    });
  });

  return Array.from(years).sort((a, b) => a - b);
};

export const buildResilienceScoreDisplayYears = (response: FarmResilienceApiResponse | null | undefined, selectedYear: number, windowSize = 5): number[] => {
  const safeWindowSize = Math.max(1, Math.trunc(windowSize));
  const availableYears = getAvailableResilienceScoreYears(response);

  const firstAvailableYear = availableYears.length ? availableYears[0] : selectedYear;
  let startYear = firstAvailableYear;

  // Keep a stable first-data-year anchored window unless selected year moves beyond it.
  if (selectedYear > firstAvailableYear + (safeWindowSize - 1)) {
    startYear = selectedYear - (safeWindowSize - 1);
  }
  if (selectedYear < startYear) {
    startYear = selectedYear;
  }

  return Array.from({ length: safeWindowSize }, (_, idx) => startYear + idx);
};
